import React, {useState, useEffect} from 'react'
import {Col, Row} from 'components/Display'
import i18n from 'i18n'
import AddSolutionView from 'views/AddSolution'
import {RouteMatch} from 'data/types'
import services from 'services'
import api from 'services/APIRequestor'
import Spinner from 'components/Spinner'
import NextSteps from 'components/NextSteps'
import CommentSection from './CommentSection'
import CommentChatHeader from 'components/CommentChatHeader'
import CommentInput from 'views/CommentInput'
import SumarizedProblem from './SumarizedProblem'
import {usePublished} from 'utils/published'
import Button from 'components/Button'
import SuggestedSolution from 'components/SuggestedSolution'
import {useFunction} from 'utils/hooks/function'
import {noImagePlaceholderBlob} from 'utils/noImagePlaceholderBlob'

type SolutionListProps = {
  match: RouteMatch<{problemId: string}>
  isFullScreen: boolean
  setFullScreen: () => void
}

const fetchSolutionSuggestion = async (problemId: number, submitSolution: (soulution: any) => void) => {
  const result = await api.get<any>(`/problems/${problemId}/suggested_solution`)
  if (result.ok) {
    const solution: string = await result.body.solutionSuggestion
    submitSolution({
      title: solution,
      description: solution,
      images: [noImagePlaceholderBlob],
    })
  }

  return null
}

export default function SolutionList(props: SolutionListProps) {
  const service = services.getSingleProblemService(props.match.params.problemId)
  const [solutions, isLoadingSolutions] = service.solutions.hook()

  const [loadingSuggestions, setLoadingSuggestions] = useState(false)
  const problem = usePublished(service.problem)

  const problemSolutionService = services.getSingleProblemService(problem!.id)
  const submitSolution = useFunction(problemSolutionService.createSolution)

  useEffect(() => {
    if (problem?.solutionsCount === 0) {
      setLoadingSuggestions(true)
      fetchSolutionSuggestion(problem!.id, submitSolution).finally(() => setLoadingSuggestions(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problem])

  if (problem == null) {
    return null
  }

  return (
    <React.Fragment>
      <Col className="flex-1 overflow-y-scroll pt-2">
        <div>
          <SumarizedProblem problem={problem} />
        </div>
        <div>
          <Row className="justify-between pb-6 px-8">
            <Col>
              <div className="text-itx-gray-dark text-xs font-bold">{i18n.t('solutions.counting', {count: problem.solutionsCount})}</div>
              <div className="text-sm text-itx-primary-darkest pt-2 text-xl font-bold ">{i18n.t('solutions.exploreSolutions')}</div>
            </Col>
            <Row className="justify-end">
              <Button
                onClick={() => {
                  setLoadingSuggestions(true)
                  fetchSolutionSuggestion(problem.id, submitSolution).finally(() => setLoadingSuggestions(false))
                }}
                style={{fontWeight: 500, paddingLeft: '4px', paddingRight: '4px'}}
                className="py-1 mr-2 bg-white border-itx-greyscale-3 text-itx-greyscale-3"
                content={i18n.t('solutions.getProposal')}
              />
              <AddSolutionView problem={problem} />
            </Row>
          </Row>
        </div>

        {isLoadingSolutions && (
          <div className="mx-auto">
            <Spinner />
          </div>
        )}

        <div className="px-8 flex-grow">
          {loadingSuggestions && (
            <Row className="items-end bg-itx-primary-lightest px-2 py-2 mb-2 border border-itx-primary-base rounded-md">
              <Spinner className="relative" color="#1E90FF" />
              <Row className="pl-2 text-itx-primary-base font-semibold">Generating solution...</Row>
            </Row>
          )}
          {solutions.map((solution) => {
            return (
              <SuggestedSolution
                key={solution.id}
                problem={problem}
                setSelectedSolution={() => services.router.selectSolution(problem, solution)}
                suggestedSolution={solution}
              />
            )
          })}
        </div>

        {problem.solutionsCount > 0 && (
          <div className="px-8">
            <NextSteps solutionsCount={problem.solutionsCount} />
          </div>
        )}
        <div className="bg-blue-itx-light mt-4 border-t flex-grow">
          <CommentSection
            threadId={problem.threadId}
            header={<CommentChatHeader user={problem.creator} i18nPlaceholder="problems.createdThisProblem" createdAt={problem.createdAt} />}
          />
        </div>
      </Col>
      <div>
        <CommentInput threadId={problem.threadId} />
      </div>
    </React.Fragment>
  )
}
